import { useCallback, useEffect, useRef } from 'react';
import { getScrollElementForEvent } from '@/_analytic/hooks/_utils/get-scroll-element-for-event';
import { getScrollBottom } from '@/_analytic/hooks/_utils/get-scroll-bottom';
import { getBottomLine } from './_utils/get-bottom-line';

type TProps = {
  content: HTMLElement | null;
  scrollElement: HTMLElement | null;
  onEvent: () => void;
};

export const useUserSawArticleEnd = ({
  content,
  onEvent,
  scrollElement,
}: TProps): void => {
  const articleEndPoint = useRef<number | null>(0);
  const scrollElementForEvent = getScrollElementForEvent(scrollElement);

  const handleUpdateCalcArticleEndPoint = useCallback(() => {
    if (!content) {
      return;
    }

    const bottomLine = getBottomLine(content);
    if (bottomLine?.offsetTop) {
      const updatedArticleEndPoint =
        bottomLine.offsetTop - (scrollElement?.offsetTop || 0);

      if (articleEndPoint.current !== null) {
        articleEndPoint.current = updatedArticleEndPoint;
      }
    }
  }, [content, scrollElement?.offsetTop]);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    const handleScroll = (): void => {
      handleUpdateCalcArticleEndPoint();
      const scrollBottom = getScrollBottom(scrollElement);

      if (!articleEndPoint.current) {
        return;
      }

      if (scrollBottom >= articleEndPoint.current) {
        articleEndPoint.current = null;
        onEvent();
      }
    };
    handleScroll();

    scrollElementForEvent?.addEventListener('scroll', handleScroll);

    return () => {
      scrollElementForEvent?.removeEventListener('scroll', handleScroll);
    };
  }, [
    onEvent,
    scrollElementForEvent,
    scrollElement,
    handleUpdateCalcArticleEndPoint,
  ]);
};
